import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';
import { useId } from 'react';
import { Icon } from '../display/Icon.tsx';
import styles from './SegmentedControl.module.css';

export interface SegmentedOption<V extends string> {
  readonly value: V;
  readonly label: string;
  readonly icon?: LucideIcon | undefined;
}

export interface SegmentedControlProps<V extends string> {
  /** Accessible name of the group. */
  readonly label: string;
  readonly value: V;
  readonly options: readonly SegmentedOption<V>[];
  readonly onChange: (value: V) => void;
  readonly fullWidth?: boolean | undefined;
  readonly className?: string | undefined;
}

/**
 * Two or three short, mutually exclusive modes. Native radio buttons underneath, so arrow keys,
 * form semantics and screen readers work without extra code.
 */
export const SegmentedControl = <V extends string>({
  label,
  value,
  options,
  onChange,
  fullWidth,
  className,
}: SegmentedControlProps<V>) => {
  const name = useId();
  return (
    <fieldset className={clsx(styles.group, fullWidth && styles.fullWidth, className)}>
      <legend className={styles.legend}>{label}</legend>
      {options.map((option) => (
        <label key={option.value} className={clsx(styles.segment, option.value === value && styles.selected)}>
          <input
            type="radio"
            className={styles.radio}
            name={name}
            value={option.value}
            checked={option.value === value}
            onChange={() => onChange(option.value)}
          />
          {option.icon && <Icon icon={option.icon} size="sm" />}
          <span className={styles.label}>{option.label}</span>
        </label>
      ))}
    </fieldset>
  );
};
